const HEIC_MIME_TYPES = new Set(['image/heic', 'image/heif']);
const HEIC_EXTENSIONS = new Set(['heic', 'heif']);

// Shared across every picker that runs a file through prepareAttachmentFile,
// so a failed HEIC conversion reads the same way everywhere.
export const UNREADABLE_IMAGE_MESSAGE =
  "We couldn't read that image. Try a JPG or PNG.";

function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() ?? '';
}

export function isHeicAttachment(file: File): boolean {
  if (file.type && HEIC_MIME_TYPES.has(file.type)) return true;
  return HEIC_EXTENSIONS.has(getFileExtension(file.name));
}

function toJpegFilename(filename: string): string {
  const ext = getFileExtension(filename);
  if (HEIC_EXTENSIONS.has(ext)) {
    return filename.slice(0, -(ext.length + 1)) + '.jpg';
  }
  if (!ext) return `${filename}.jpg`;
  return filename.replace(/\.[^.]+$/, '.jpg');
}

async function convertHeicToJpeg(file: File): Promise<File> {
  const { default: heic2any } = await import(
    /* webpackChunkName: 'heic2any' */ 'heic2any'
  );
  const converted = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.92,
  });
  const blob = Array.isArray(converted) ? converted.at(0) : converted;
  if (!blob) {
    throw new Error('HEIC conversion returned no data');
  }
  return new File([blob], toJpegFilename(file.name), {
    type: 'image/jpeg',
    lastModified: file.lastModified,
  });
}

// HEIC/HEIF are accepted at the picker but browsers and the model cannot use
// them directly — convert to JPEG before upload so previews and vision input work.
export async function prepareAttachmentFile(file: File): Promise<File> {
  if (!isHeicAttachment(file)) return file;
  return convertHeicToJpeg(file);
}
